import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedRoofSoffit, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import { GABLE_TRIM_OFFSET_Z } from "common/constants/roofTrimConstants";
import type { RWTable, Table } from "common/data/table";
import objectMap from "common/utils/objectMap";
import { mergedPropLibraryNew } from "doc/propTools";
import { getExteriorWallSubstrateOffset } from "doc/utils/wallTypeTools";
import type { NodeBSP } from "geom/bspTypes";
import getTargetedPorchSettings from "../elements/porch/getTargetedPorchSettings";
import { buildGableWallOverlapBSPs, clipRoofSystem, clipWallSystem } from "../elements/roofs/roofClipping";
import { buildRoofPerimetersTrims } from "../elements/roofs/roofTrims";
import { buildSubRoofWallDiscardBSPs, buildWallOverlapBSPs, collectRoofWallCuts } from "../elements/roofs/roofWalls";
import { clipWallsWithRoofClippingOperations } from "../elements/roofs/wallPlanesRoofClipping";
import { buildRoofSystemHalfStoryDiscardBSPs } from "../elements/walls/halfStories";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import { memoAsync } from "../utils/memo";

function select({
  levels,
  roofSystemLevels,
  roofSystems,
  roofSoffits,
  dormers,
  windowPlacements: windows,
  doorPlacements: doors,
  wallCuts,
  settings,
  settingsSets,
  optionChoices,
  walls,
  rooms,
  blocks,
  doc: { assets, settings: docSettings, entitySettings, profiles },
  porches,
  enabledFeatures,
}: Pick<
  Building,
  | "levels"
  | "roofSystemLevels"
  | "roofSystems"
  | "roofSoffits"
  | "dormers"
  | "windowPlacements"
  | "doorPlacements"
  | "wallCuts"
  | "settings"
  | "settingsSets"
  | "optionChoices"
  | "walls"
  | "rooms"
  | "blocks"
  | "doc"
  | "porches"
  | "enabledFeatures"
>) {
  return {
    levels,
    roofSystemLevels,
    roofSystems,
    dormers,
    windows,
    doors,
    wallCuts,
    propSettings: settings.props,
    assets,
    wallSettings: settings.wall,
    profileSource: {
      settingsToCatalogs: enabledFeatures.settingsToCatalogs,
      profiles,
      profileSettings: settings.profile,
    },
    exteriorTrimSettings: settings.exteriorTrim,
    porchSettings: settings.porch,
    roofSoffits,
    rearPorchSettings: settings.rearPorch,
    walls,
    rooms,
    porches,
    blocks,
    settingsSets,
    optionChoices,
    docSettings,
    entitySettings,
    settings,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  profileSource: "shallow",
});

async function generate({
  levels,
  roofSystemLevels,
  roofSystems,
  roofSoffits,
  dormers,
  windows,
  doors,
  wallCuts,
  wallSettings,
  propSettings,
  assets,
  profileSource,
  exteriorTrimSettings,
  porchSettings,
  rearPorchSettings,
  walls,
  blocks,
  rooms,
  porches,
  settingsSets,
  optionChoices,
  docSettings,
  entitySettings,
  settings,
  enabledFeatures,
}: ReturnType<typeof select>): Promise<Pick<Building, "roofSystems" | "walls" | "roofSoffits" | "issues">> {
  const issues: BuildingGenerationIssue[] = [];

  if (!roofSystems) {
    return { roofSystems, walls, roofSoffits, issues };
  }

  const newRoofSystems: RWTable<GeneratedRoofSystem> = {};
  const roofWallCuts = collectRoofWallCuts(windows, doors, wallCuts);
  const wallOverlapForFriezeBoardBSPsTable: RWTable<NodeBSP[]> = {};
  const gableWallOverlapBSPsTable: RWTable<NodeBSP[]> = {};
  const targetedPorchSettings = getTargetedPorchSettings(
    { settingsSets, optionChoices, settings, rooms },
    { settings: docSettings, entitySettings },
  );

  const roofWallDiscardBSPs = buildSubRoofWallDiscardBSPs(
    levels,
    walls,
    rooms,
    porchSettings,
    rearPorchSettings,
    targetedPorchSettings,
    getExteriorWallSubstrateOffset(wallSettings),
  );

  for (const roofId in roofSystems) {
    const roofSystem = roofSystems[roofId];
    const { clipContext, roofs } = roofSystem;
    const { insideBSP } = clipContext;

    wallOverlapForFriezeBoardBSPsTable[roofId] = buildWallOverlapBSPs(
      walls,
      blocks,
      insideBSP,
      true,
      Math.abs(GABLE_TRIM_OFFSET_Z),
    );

    gableWallOverlapBSPsTable[roofId] = buildGableWallOverlapBSPs(roofs, wallSettings);

    newRoofSystems[roofId] = clipRoofSystem({
      roofSystem: roofSystems[roofId],
      dormers,
      levels,
      roofSystemLevels,
      walls,
      roofWallCuts,
      roofWallDiscardBSPs,
      wallSettings,
      profileSource,
      exteriorTrimSettings,
      blocks,
      wallOverlapForFriezeBoardBSPs: wallOverlapForFriezeBoardBSPsTable[roofId],
      gableWallOverlapBSPs: gableWallOverlapBSPsTable[roofId],
      issues,
    });
  }

  if (!walls) {
    return { roofSystems, walls, roofSoffits, issues };
  }

  const blockIsHalfStory = objectMap(blocks, (x) => x.isHalfStory === true);

  const roofSystemsHalfStoryClipping: RWTable<Table<NodeBSP>> = {};
  for (const roofId in newRoofSystems) {
    const roof = newRoofSystems[roofId];

    // Keep subRoofId -> BSP mapping so that clipping can filter half-story
    // discard BSPs by the sub-roof's level. Without that filter, a sub-roof
    // on a lower level would over-clip walls on a half-story above, because
    // its "above-the-roof" discard volume extends vertically through the
    // half-story territory.
    const bsps = buildRoofSystemHalfStoryDiscardBSPs(roof);
    roofSystemsHalfStoryClipping[roofId] = bsps;
    newRoofSystems[roofId] = { ...roof, halfStoryDiscardBSPs: bsps };
  }

  const newWalls: RWTable<WallSystem> = {};
  for (const levelId in walls) {
    if (enabledFeatures.wallPlanes) {
      /**
       * TODO WALLS V2 - all level wall clipping is disabled for now
       * until we implement correct clipping for wall planes
       */
      newWalls[levelId] = clipWallsWithRoofClippingOperations(walls[levelId], newRoofSystems);

      // clip the roof wall systems
      for (const roofId in newRoofSystems) {
        const roof = newRoofSystems[roofId];
        const { roofs } = roof;
        for (const subRoofId in roofs) {
          const subRoof = roofs[subRoofId];
          if (subRoof.roofWallSystem) {
            const newRoofWalls: WallSystem = clipWallsWithRoofClippingOperations(
              subRoof.roofWallSystem.wallSystem,
              newRoofSystems,
            );
            newRoofSystems[roofId] = {
              ...newRoofSystems[roofId],
              roofs: {
                ...newRoofSystems[roofId].roofs,
                [subRoofId]: { ...subRoof, roofWallSystem: { ...subRoof.roofWallSystem, wallSystem: newRoofWalls } },
              },
            };
          }
        }
      }
    } else {
      newWalls[levelId] = clipWallSystem(
        levels[levelId],
        walls[levelId],
        newRoofSystems,
        roofSystemsHalfStoryClipping,
        blockIsHalfStory,
        rooms,
        wallSettings,
        issues,
      );
    }
  }

  const halfStoryBSPsTable: RWTable<NodeBSP[]> = {};

  for (const levelId in roofSystemLevels) {
    halfStoryBSPsTable[levelId] =
      roofSystemLevels[levelId].halfStoriesBSP !== null ? [roofSystemLevels[levelId].halfStoriesBSP] : [];
  }

  const mergedPropLibrary = mergedPropLibraryNew(assets);

  // this needs to be done after the walls are clipped as it uses the final wall system
  // to re-project the frieze board rails
  for (const roofId in newRoofSystems) {
    newRoofSystems[roofId] = await buildRoofPerimetersTrims({
      roofSystem: newRoofSystems[roofId],
      walls,
      porches,
      wallSettings,
      profileSource,
      propSettings,
      mergedPropLibrary,
      wallOverlapForFriezeBoardBSPs: wallOverlapForFriezeBoardBSPsTable[roofId],
      gableWallOverlapBSPs: gableWallOverlapBSPsTable[roofId],
      halfStoryBSPsTable,
      issues,
    });
  }

  // collect all the soffits from the roof systems
  const newRoofSoffits: RWTable<GeneratedRoofSoffit> = {};
  for (const roofId in newRoofSystems) {
    const roof = newRoofSystems[roofId];
    for (const subRoofId in roof.roofs) {
      const subRoof = roof.roofs[subRoofId];
      for (const soffit of subRoof.soffits) {
        newRoofSoffits[soffit.id] = soffit;
      }
    }
  }

  return {
    roofSystems: newRoofSystems,
    walls: newWalls,
    roofSoffits: newRoofSoffits,
    issues,
  };
}

export default memoAsync(select, generate, compare);
